import Backbone from "backbone";
import Parse from "parse";
import _ from "underscore";
import BaseCollection from "../collections/BaseCollection";
import FeaturedStoryModel from "../models/FeaturedStory";
import ObservingList from "./ObservingList";

// Cache configuration
const CACHE_KEY_STORIES_LIST = "featuredStoriesList";
const CACHE_EXPIRY_MS = 24 * 60 * 60 * 1000; // 24 hours
const RESYNC_DEBOUNCE_MS = 1000;
const SUBSCRIPTION_READY_TIMEOUT_MS = 15000;
const RESYNC_CONCURRENCY = 3;
const RESYNC_RETRY_BASE_MS = 5000;
const RESYNC_RETRY_MAX_MS = 60000;

const registry = new Map();
let recoveryInitialized = false;
let recovering = false;
let transportConnected = false;
let recoveryGeneration = 0;
let resyncTimer = null;
let readinessTimer = null;
let autoRetryTimer = null;
let autoRetryAttempt = 0;

function objectTimestamp(object) {
  const value = object?.updatedAt || object?.get?.("updatedAt");
  const timestamp = value instanceof Date ? value.getTime() : Date.parse(value);
  return Number.isNaN(timestamp) ? 0 : timestamp;
}

function upsert(collection, object) {
  const index = collection.findIndex((item) => item.id === object.id);
  if (index === -1) {
    collection.push(object);
    return;
  }

  if (objectTimestamp(collection[index]) > objectTimestamp(object)) return;
  collection.splice(index, 1, object);
}

function applyEvent(collection, event) {
  if (event.type === "delete" || event.type === "leave") {
    collection.remove(event.object.id);
    return;
  }
  upsert(collection, event.object);
}

function clearRecoveryTimers() {
  if (resyncTimer) {
    clearTimeout(resyncTimer);
    resyncTimer = null;
  }
  if (readinessTimer) {
    clearTimeout(readinessTimer);
    readinessTimer = null;
  }
  if (autoRetryTimer) {
    clearTimeout(autoRetryTimer);
    autoRetryTimer = null;
  }
}

// A subscription whose original subscribe() rejected never entered the Parse
// client's map, so the SDK's resubscribe() will never recover it. Retry paths
// re-run subscribe() for those entries so retry can heal every state.
function resubscribeMissing() {
  registry.forEach((entry) => {
    if (entry.needsSync && !entry.subscription) {
      hydrateModelData.subscribeEntry(entry);
    }
  });
}

function scheduleAutoRetry() {
  if (!recovering || autoRetryTimer) return;

  const generation = recoveryGeneration;
  const delay = Math.min(
    RESYNC_RETRY_MAX_MS,
    RESYNC_RETRY_BASE_MS * 2 ** autoRetryAttempt,
  );
  autoRetryAttempt += 1;
  autoRetryTimer = setTimeout(() => {
    autoRetryTimer = null;
    if (generation !== recoveryGeneration || !recovering) return;
    resubscribeMissing();
    if (transportConnected && allSubscriptionsReady()) {
      scheduleResync();
    } else {
      scheduleAutoRetry();
    }
  }, delay);
}

function syncFailures(entries, reason) {
  Backbone.trigger("livequery:sync-failed", {
    failures: entries.map((entry) => ({
      collectionName: entry.collectionName,
      error: entry.subscriptionError || reason,
    })),
  });
  scheduleAutoRetry();
}

function allSubscriptionsReady() {
  const entries = Array.from(registry.values()).filter(
    (entry) => entry.needsSync,
  );
  return (
    entries.length > 0 &&
    entries.every(
      (entry) =>
        entry.subscription &&
        entry.isSubscriptionOpen &&
        !entry.subscriptionError,
    )
  );
}

function scheduleResync() {
  if (!recovering || !transportConnected || !allSubscriptionsReady()) return;

  if (readinessTimer) {
    clearTimeout(readinessTimer);
    readinessTimer = null;
  }
  if (resyncTimer) clearTimeout(resyncTimer);

  const generation = recoveryGeneration;
  resyncTimer = setTimeout(() => {
    resyncTimer = null;
    hydrateModelData.resyncAll(generation);
  }, RESYNC_DEBOUNCE_MS);
}

function handleGlobalClose() {
  recovering = true;
  transportConnected = false;
  recoveryGeneration += 1;
  autoRetryAttempt = 0;
  clearRecoveryTimers();

  registry.forEach((entry) => {
    entry.needsSync = true;
    entry.isSubscriptionOpen = false;
    entry.subscriptionError = null;
    entry.pendingEvents = [];
  });
}

function handleConnected({ recovering: wasRecovering } = {}) {
  transportConnected = true;
  if (!wasRecovering || !recovering) return;

  readinessTimer = setTimeout(() => {
    readinessTimer = null;
    const unready = Array.from(registry.values()).filter(
      (entry) =>
        entry.needsSync &&
        (!entry.subscription ||
          !entry.isSubscriptionOpen ||
          entry.subscriptionError),
    );
    if (unready.length > 0) {
      syncFailures(
        unready,
        new Error("LiveQuery subscriptions did not reopen in time."),
      );
    }
  }, SUBSCRIPTION_READY_TIMEOUT_MS);

  scheduleResync();
}

function handleRetry() {
  if (!recovering || !transportConnected) return;
  resubscribeMissing();
  scheduleResync();
}

function getCached(key) {
  try {
    const cached = localStorage.getItem(key);
    if (!cached) return null;

    const { data, timestamp } = JSON.parse(cached);
    if (Date.now() - timestamp > CACHE_EXPIRY_MS) {
      localStorage.removeItem(key);
      return null;
    }
    return data;
  } catch {
    return null;
  }
}

function setCache(key, data) {
  try {
    localStorage.setItem(key, JSON.stringify({ data, timestamp: Date.now() }));
  } catch {
    // localStorage full or unavailable - ignore
  }
}

const hydrateModelData = {
  hydrate: function (model) {
    const order = model?.query?.order || "descending";
    const orderOn = model?.query?.orderOn || "updatedAt";
    const global = model?.query?.global || false;
    const limit = model?.query?.limit || 10000;
    const includes = model?.query?.includes || [];
    const query = this.createQuery(
      model.model,
      order,
      orderOn,
      global,
      limit,
      includes,
    );

    return this.hydrateCollection({
      model: model.model,
      query,
      collectionName: model.collectionName,
    });
  },

  createQuery: function (
    model,
    order = "descending",
    orderOn = "updatedAt",
    global = false,
    limit = 10000,
    includes = [],
  ) {
    const query = global
      ? new Parse.Query(model).limit(limit)[order](orderOn)
      : new Parse.Query(model)
          .equalTo("user", this.currentUser())
          .notEqualTo("deleted", true)
          .limit(limit)
          [order](orderOn);

    // Add includes for related objects
    includes.forEach((field) => query.include(field));

    return query;
  },

  currentUser: () => Portal.Current.user.current(),

  hydrateCollection: function (props = {}) {
    const { model, query, collectionName } = props;
    const collection = this.createCurrentCollection([], collectionName);
    const entry = {
      collectionName,
      model,
      query,
      collection,
      subscription: null,
      isSubscriptionOpen: false,
      subscriptionError: null,
      subscribePending: false,
      needsSync: false,
      pendingEvents: [],
    };
    registry.set(collectionName, entry);

    return query.find().then(
      (result) => {
        result.forEach((item) => collection.add(item));
        this.subscribeEntry(entry);
        return result;
      },
      (error) => {
        Backbone.trigger("errors:parseReturned", error);
      },
    );
  },

  subscribeEntry: function (entry) {
    if (entry.subscription || entry.subscribePending) return;

    entry.subscribePending = true;
    entry.subscriptionError = null;
    entry.query.subscribe().then(
      (subscription) => {
        entry.subscribePending = false;
        entry.subscription = subscription;
        this.subscribeToAll(
          subscription,
          entry.collectionName,
          entry.collection,
          entry.model,
        );
      },
      (error) => {
        entry.subscribePending = false;
        entry.subscriptionError = error;
        Backbone.trigger("errors:parseReturned", error);
        if (recovering) syncFailures([entry], error);
      },
    );
  },

  createCurrentCollection: (collectionData, collectionName) => {
    Portal.Current.Collections[collectionName] = _.extend(
      collectionData,
      BaseCollection,
    );
    return Portal.Current.Collections[collectionName];
  },

  subscribeToAll: function (subscription, collectionName, collection, model) {
    const entry = registry.get(collectionName);
    this.subscribeToOpen(subscription, entry);
    this.subscribeToClose(subscription, entry);
    this.subscribeToError(subscription, entry);
    this.subscribeToDelete(subscription, collectionName, collection);
    this.subscribeToLeave(subscription, collectionName, collection);
    this.subscribeToUpdate(subscription, collectionName, collection);
    this.subscribeToEnter(subscription, collectionName, collection, model);
    this.subscribeToCreate(subscription, collectionName, collection, model);
  },

  subscribeToOpen: (subscription, entry) => {
    subscription.on("open", () => {
      if (!entry) return;
      entry.isSubscriptionOpen = true;
      entry.subscriptionError = null;
      scheduleResync();
    });
  },

  subscribeToClose: (subscription, entry) => {
    subscription.on("close", () => {
      if (entry) entry.isSubscriptionOpen = false;
    });
  },

  subscribeToError: (subscription, entry) => {
    subscription.on("error", (error) => {
      if (!entry) return;
      entry.subscriptionError = error;
      if (recovering && transportConnected) syncFailures([entry], error);
    });
  },

  handleCollectionEvent: (collectionName, collection, type, object) => {
    const entry = registry.get(collectionName);
    const event = { type, object };

    if (entry?.needsSync) {
      entry.pendingEvents.push(event);
      return;
    }

    applyEvent(collection, event);
    collection.trigger("change");
  },

  subscribeToDelete: (subscription, collectionName, collection) => {
    subscription.on("delete", (object) => {
      try {
        hydrateModelData.handleCollectionEvent(
          collectionName,
          collection,
          "delete",
          object,
        );
      } catch (error) {
        console.warn("Error handling delete event:", error);
      }
    });
  },

  subscribeToLeave: (subscription, collectionName, collection) => {
    subscription.on("leave", (object) => {
      hydrateModelData.handleCollectionEvent(
        collectionName,
        collection,
        "leave",
        object,
      );
    });
  },

  subscribeToUpdate: (subscription, collectionName, collection) => {
    subscription.on("update", (object) => {
      hydrateModelData.handleCollectionEvent(
        collectionName,
        collection,
        "update",
        object,
      );
    });
  },

  subscribeToEnter: (subscription, collectionName, collection) => {
    subscription.on("enter", (object) => {
      hydrateModelData.handleCollectionEvent(
        collectionName,
        collection,
        "enter",
        object,
      );
    });
  },

  subscribeToCreate: (subscription, collectionName, collection) => {
    subscription.on("create", (object) => {
      hydrateModelData.handleCollectionEvent(
        collectionName,
        collection,
        "create",
        object,
      );
    });
  },

  initRecovery: () => {
    if (recoveryInitialized) return;
    recoveryInitialized = true;
    Backbone.on("livequery:closed", handleGlobalClose);
    Backbone.on("livequery:connected", handleConnected);
    Backbone.on("livequery:retry", handleRetry);
  },

  resyncCollection: async (entry) => {
    const snapshot = await entry.query.find();
    return { entry, snapshot };
  },

  resyncAll: async function (generation = recoveryGeneration) {
    if (
      !recovering ||
      !transportConnected ||
      generation !== recoveryGeneration
    ) {
      return;
    }

    const entries = Array.from(registry.values()).filter(
      (entry) => entry.needsSync,
    );
    if (entries.length === 0) return;

    const unready = entries.filter(
      (entry) =>
        !entry.subscription ||
        !entry.isSubscriptionOpen ||
        entry.subscriptionError,
    );
    if (unready.length > 0) {
      syncFailures(
        unready,
        new Error("LiveQuery subscriptions are not ready."),
      );
      return;
    }

    Backbone.trigger("livequery:syncing");

    const results = new Array(entries.length);
    let nextIndex = 0;
    const worker = async () => {
      while (nextIndex < entries.length) {
        const index = nextIndex;
        nextIndex += 1;
        try {
          results[index] = {
            status: "fulfilled",
            value: await this.resyncCollection(entries[index]),
          };
        } catch (reason) {
          results[index] = { status: "rejected", reason };
        }
      }
    };

    await Promise.all(
      Array.from(
        { length: Math.min(RESYNC_CONCURRENCY, entries.length) },
        worker,
      ),
    );

    if (generation !== recoveryGeneration || !recovering) return;

    const resultByName = new Map(
      results.map((result, index) => [entries[index].collectionName, result]),
    );
    const observingListsResult = resultByName.get("observingLists");
    const observingListAuxsResult = resultByName.get("observingListAuxs");
    if (
      observingListsResult &&
      observingListAuxsResult &&
      (observingListsResult.status === "rejected" ||
        observingListAuxsResult.status === "rejected")
    ) {
      if (observingListsResult.status === "fulfilled") {
        observingListsResult.status = "rejected";
        observingListsResult.reason = new Error(
          "Deferred until observingListAuxs also resyncs.",
        );
      }
      if (observingListAuxsResult.status === "fulfilled") {
        observingListAuxsResult.status = "rejected";
        observingListAuxsResult.reason = new Error(
          "Deferred until observingLists also resyncs.",
        );
      }
    }

    const appliedEntries = [];
    const failures = [];

    results.forEach((result, index) => {
      const entry = entries[index];
      if (result.status === "rejected") {
        failures.push({
          collectionName: entry.collectionName,
          error: result.reason,
        });
        return;
      }

      entry.collection.splice(0, entry.collection.length);
      result.value.snapshot.forEach((object) =>
        upsert(entry.collection, object),
      );
      entry.pendingEvents.forEach((event) =>
        applyEvent(entry.collection, event),
      );
      entry.pendingEvents = [];
      entry.needsSync = false;
      appliedEntries.push(entry);
    });

    const derivedInputsReady = ["observingLists", "observingListAuxs"].every(
      (name) => !registry.get(name)?.needsSync,
    );
    if (
      derivedInputsReady &&
      appliedEntries.some((entry) =>
        ["observingLists", "observingListAuxs"].includes(entry.collectionName),
      )
    ) {
      ObservingList.setAuxLists();
    }

    appliedEntries.forEach((entry) => entry.collection.trigger("change"));

    if (failures.length > 0) {
      const invalidSessionFailure = failures.find(
        ({ error }) => error?.code === Parse.Error.INVALID_SESSION_TOKEN,
      );
      if (invalidSessionFailure) {
        Backbone.trigger("errors:parseReturned", invalidSessionFailure.error);
      }
      Backbone.trigger("livequery:sync-failed", { failures });
      scheduleAutoRetry();
      return;
    }

    recovering = false;
    autoRetryAttempt = 0;
    clearRecoveryTimers();
    Backbone.trigger("livequery:restored");
  },

  reset: () => {
    if (recoveryInitialized) {
      Backbone.off("livequery:closed", handleGlobalClose);
      Backbone.off("livequery:connected", handleConnected);
      Backbone.off("livequery:retry", handleRetry);
    }
    recoveryInitialized = false;
    recovering = false;
    transportConnected = false;
    recoveryGeneration = 0;
    autoRetryAttempt = 0;
    clearRecoveryTimers();
    registry.clear();
  },

  getFeaturedStoriesList: async () => {
    const cached = getCached(CACHE_KEY_STORIES_LIST);
    if (cached) return cached;

    const featuredStoriesList = await Parse.Cloud.run("getFeaturedStoriesList");
    setCache(CACHE_KEY_STORIES_LIST, featuredStoriesList);
    return featuredStoriesList;
  },

  populateFeaturedArticleCollection: function () {
    const collection = this.createCurrentCollection([], "featuredStories");
    this.getFeaturedStoriesList().then(
      (result) => {
        if (result.length > 0) {
          result.forEach((story) => {
            const storyModel = new FeaturedStoryModel({
              id: story.id,
              link: story.link,
              date: story.date,
              title: story.title,
              excerpt: story.excerpt,
              urlSlug: story.urlSlug,
            });
            collection.add(storyModel);
          });
          collection.trigger("change");
        }
      },
      (error) => {
        Backbone.trigger("errors:parseReturned", error);
      },
    );
  },

  getAstrobinImagesOfTheDay: async (limit = 1, offset = 0) => {
    const astrobinImageOfTheDay = await Parse.Cloud.run(
      "getAstrobinImageOfTheDay",
      {
        limit,
        offset,
      },
    );

    return astrobinImageOfTheDay;
  },

  // TODO: This is a temporary solution to get the Astrobin image of the day. It calls the API directly
  populateAstrobinImageOfTheDayCollection: function (limit = 1, offset = 0) {
    const collection = this.createCurrentCollection(
      [],
      "astrobinImageOfTheDay",
    );

    this.getAstrobinImagesOfTheDay(limit, offset).then(
      (result) => {
        const images = Array.isArray(result) && result.length > 0 ? result : [];
        images.forEach((image) => collection.add(image));
        collection.trigger("change");
      },
      (error) => {
        Backbone.trigger("errors:parseReturned", error);
      },
    );
  },
};

export { RESYNC_RETRY_BASE_MS };
export default hydrateModelData;
